import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import { alpha, useTheme } from '@material-ui/core/styles';
import { SRVSTM_IFRAME_PLAYER_SRC } from '../constants/streamingPlayer';

const StreamingPlayerEmbed = ({
  lazyLoad = true,
  showStopControl = false,
  title = 'TNO Network — señal en vivo',
  playLabel = 'Ver señal en vivo',
  sx = {},
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [isPlaying, setIsPlaying] = useState(!lazyLoad);
  const [isLoading, setIsLoading] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
    setIsLoading(true);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        bgcolor: '#000',
        ...sx,
      }}
    >
      {!isPlaying && lazyLoad && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(145deg, #0d1b2a 0%, #1a237e 55%, ${alpha(primary, 0.85)} 100%)`,
            px: 1.5,
            textAlign: 'center',
          }}
        >
          <IconButton
            onClick={handlePlay}
            aria-label={playLabel}
            sx={{
              width: 56,
              height: 56,
              mb: 1,
              bgcolor: primary,
              color: '#fff',
              boxShadow: `0 8px 24px ${alpha(primary, 0.45)}`,
              '&:hover': { bgcolor: theme.palette.primary.dark, transform: 'scale(1.05)' },
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.35 }}>
            {playLabel}
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.75)',
          }}
        >
          <CircularProgress size={28} sx={{ color: primary, mb: 1 }} />
          <Typography sx={{ color: '#fff', fontSize: '0.75rem' }}>Cargando stream…</Typography>
        </Box>
      )}

      {isPlaying && (
        <Box
          component="iframe"
          src={SRVSTM_IFRAME_PLAYER_SRC}
          title={title}
          scrolling="no"
          frameBorder={0}
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          onLoad={() => setIsLoading(false)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      )}

      {isPlaying && lazyLoad && showStopControl && !isLoading && (
        <IconButton
          onClick={handleStop}
          size="small"
          aria-label="Detener transmisión"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            zIndex: 3,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          <PauseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

StreamingPlayerEmbed.propTypes = {
  lazyLoad: PropTypes.bool,
  showStopControl: PropTypes.bool,
  title: PropTypes.string,
  playLabel: PropTypes.string,
  sx: PropTypes.object,
};

export default StreamingPlayerEmbed;
